/** @format */

import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableHead,
    TableRow,
    CircularProgress,
    Chip,
    Stack,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
} from "@mui/material";
import {
    Refresh,
    CheckCircleOutline,
    CloseOutlined,
    OpenInNew,
} from "@mui/icons-material";

import { selectActiveBusinessId } from "../../redux/selectors/collectSelector";
import {
    fetchSegmentCounts,
    fetchRecommendations,
    recomputeIntelligence,
    applyRecommendation,
    dismissRecommendation,
} from "../../redux/services/collectIntelligenceService";
import {
    ISegmentCounts,
    IRecommendation,
    CustomerSegment,
    CUSTOMER_SEGMENT_LABELS,
    RECOMMENDATION_TYPE_LABELS,
} from "../../utility/types/collectTypes";
import { useToast } from "../../providers/ToastProvider";
import {
    CollectPageWrapper,
    CollectContentArea,
    CollectSectionCard,
    CollectSectionHeader,
    CollectSectionTitle,
    CollectSectionSubtitle,
    CollectTableContainer,
    CollectTableHeadCell,
    CollectTableBodyCell,
    CollectEmptyState,
    CollectEmptyTitle,
    CollectEmptyHint,
    CollectPrimaryButton,
    CollectGhostButton,
    CollectKpiCard,
    CollectKpiLabel,
    CollectKpiValue,
    CollectKpiHint,
    FlexBetween,
} from "./styles";

// ─────────────────────────────────────────────────────────────────────────
// CollectRecommendations (Stage 2I) — customer behaviour segmentation +
// actionable recommendations. Segments group customers by how they pay; each
// recommendation suggests the next best step. Applying a recommendation
// creates an internal operator Task, it NEVER messages the customer.
// ─────────────────────────────────────────────────────────────────────────

const SEGMENT_ORDER: CustomerSegment[] = [
    "delinquent",
    "disputer",
    "unresponsive",
    "at_risk",
    "occasional_late",
    "reliable",
    "new",
];

const SEGMENT_ACCENTS: Record<CustomerSegment, string> = {
    delinquent: "#dc2626",
    disputer: "#7c3aed",
    unresponsive: "#ea580c",
    at_risk: "#f59e0b",
    occasional_late: "#0ea5e9",
    reliable: "#16a34a",
    new: "#94a3b8",
};

const PRIORITY_COLORS: Record<string, string> = {
    urgent: "#dc2626",
    high: "#f59e0b",
    medium: "#0ea5e9",
    low: "#94a3b8",
};

const CollectRecommendations: React.FC = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const businessId = useSelector(selectActiveBusinessId);

    const [segments, setSegments] = useState<ISegmentCounts | null>(null);
    const [recommendations, setRecommendations] = useState<IRecommendation[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [recomputing, setRecomputing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [pendingId, setPendingId] = useState<string | null>(null);

    // Dismiss dialog
    const [dismissTarget, setDismissTarget] = useState<IRecommendation | null>(null);
    const [dismissReason, setDismissReason] = useState("");

    const load = useCallback(async () => {
        if (!businessId) return;
        setLoading(true);
        setError(null);
        try {
            const [counts, recs] = await Promise.all([
                fetchSegmentCounts(businessId),
                fetchRecommendations(businessId, { status: "pending", limit: 100 }),
            ]);
            setSegments(counts);
            setRecommendations(recs.items || []);
            setTotal(recs.total || 0);
        } catch (err: any) {
            setError(err.message || "Could not load recommendations");
        } finally {
            setLoading(false);
        }
    }, [businessId]);

    useEffect(() => {
        load();
    }, [load]);

    const handleRecompute = async () => {
        if (!businessId) return;
        setRecomputing(true);
        try {
            const res = await recomputeIntelligence(businessId);
            toast.success(
                `Analysed ${res.behavior.profiles} customer(s) — ${res.recommendations.created} new recommendation(s).`,
            );
            await load();
        } catch (err: any) {
            toast.error(err.message || "Could not recompute");
        } finally {
            setRecomputing(false);
        }
    };

    const handleApply = async (rec: IRecommendation) => {
        if (!businessId) return;
        setPendingId(rec._id);
        try {
            await applyRecommendation(businessId, rec._id);
            toast.success("Recommendation applied — a task was created.");
            setRecommendations((prev) => prev.filter((r) => r._id !== rec._id));
            setTotal((t) => Math.max(0, t - 1));
        } catch (err: any) {
            toast.error(err.message || "Could not apply recommendation");
        } finally {
            setPendingId(null);
        }
    };

    const handleDismiss = async () => {
        if (!businessId || !dismissTarget) return;
        const target = dismissTarget;
        setPendingId(target._id);
        try {
            await dismissRecommendation(businessId, target._id, dismissReason.trim() || undefined);
            toast.success("Recommendation dismissed.");
            setRecommendations((prev) => prev.filter((r) => r._id !== target._id));
            setTotal((t) => Math.max(0, t - 1));
            setDismissTarget(null);
            setDismissReason("");
        } catch (err: any) {
            toast.error(err.message || "Could not dismiss recommendation");
        } finally {
            setPendingId(null);
        }
    };

    return (
        <CollectPageWrapper>
            <CollectContentArea>
                {/* Segment overview */}
                <CollectSectionCard>
                    <CollectSectionHeader>
                        <FlexBetween>
                            <Box>
                                <CollectSectionTitle>
                                    Customer segments
                                </CollectSectionTitle>
                                <CollectSectionSubtitle>
                                    How your customers behave, grouped by payment
                                    patterns. Refresh to re-analyse with the latest data.
                                </CollectSectionSubtitle>
                            </Box>
                            <CollectPrimaryButton
                                startIcon={
                                    recomputing ? (
                                        <CircularProgress size={16} color="inherit" />
                                    ) : (
                                        <Refresh />
                                    )
                                }
                                onClick={handleRecompute}
                                disabled={recomputing || !businessId}
                            >
                                {recomputing ? "Analysing…" : "Refresh analysis"}
                            </CollectPrimaryButton>
                        </FlexBetween>
                    </CollectSectionHeader>

                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "repeat(2, 1fr)",
                                sm: "repeat(3, 1fr)",
                                md: "repeat(7, 1fr)",
                            },
                            gap: 1.5,
                            mt: 2,
                        }}
                    >
                        {SEGMENT_ORDER.map((seg) => (
                            <CollectKpiCard key={seg} accent={SEGMENT_ACCENTS[seg]}>
                                <CollectKpiLabel>
                                    {CUSTOMER_SEGMENT_LABELS[seg]}
                                </CollectKpiLabel>
                                <CollectKpiValue>
                                    {segments ? segments[seg] ?? 0 : "—"}
                                </CollectKpiValue>
                                <CollectKpiHint>customers</CollectKpiHint>
                            </CollectKpiCard>
                        ))}
                    </Box>
                </CollectSectionCard>

                {/* Recommendations */}
                <CollectSectionCard>
                    <CollectSectionHeader>
                        <Box>
                            <CollectSectionTitle>
                                Recommendations{" "}
                                {total > 0 && (
                                    <Chip
                                        size="small"
                                        label={total}
                                        sx={{ ml: 1, fontWeight: 700 }}
                                    />
                                )}
                            </CollectSectionTitle>
                            <CollectSectionSubtitle>
                                Suggested next steps. Applying one creates an internal
                                task for your team, it never contacts the customer
                                automatically.
                            </CollectSectionSubtitle>
                        </Box>
                    </CollectSectionHeader>

                    {error && (
                        <Typography sx={{ color: "#dc2626", mt: 2 }}>{error}</Typography>
                    )}

                    {loading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                            <CircularProgress />
                        </Box>
                    ) : recommendations.length === 0 ? (
                        <CollectEmptyState>
                            <CollectEmptyTitle>No recommendations yet</CollectEmptyTitle>
                            <CollectEmptyHint>
                                Click “Refresh analysis” to scan your customers and
                                surface suggested actions.
                            </CollectEmptyHint>
                        </CollectEmptyState>
                    ) : (
                        <CollectTableContainer sx={{ mt: 2 }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <CollectTableHeadCell>
                                            Customer
                                        </CollectTableHeadCell>
                                        <CollectTableHeadCell>
                                            Recommendation
                                        </CollectTableHeadCell>
                                        <CollectTableHeadCell>
                                            Why
                                        </CollectTableHeadCell>
                                        <CollectTableHeadCell>
                                            Priority
                                        </CollectTableHeadCell>
                                        <CollectTableHeadCell align="right">
                                            Actions
                                        </CollectTableHeadCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {recommendations.map((rec) => {
                                        const busy = pendingId === rec._id;
                                        return (
                                            <TableRow key={rec._id} hover>
                                                <CollectTableBodyCell>
                                                    <Stack
                                                        direction="row"
                                                        spacing={0.5}
                                                        alignItems="center"
                                                    >
                                                        <Typography
                                                            sx={{ fontWeight: 600 }}
                                                        >
                                                            {rec.customerDisplayName ||
                                                                "Customer"}
                                                        </Typography>
                                                        <Tooltip title="Open customer">
                                                            <OpenInNew
                                                                fontSize="small"
                                                                sx={{
                                                                    cursor: "pointer",
                                                                    color: "#94a3b8",
                                                                }}
                                                                onClick={() =>
                                                                    navigate(
                                                                        `/collect/customers/${rec.customerId}`,
                                                                    )
                                                                }
                                                            />
                                                        </Tooltip>
                                                    </Stack>
                                                    <Chip
                                                        size="small"
                                                        label={
                                                            CUSTOMER_SEGMENT_LABELS[
                                                            rec.segment
                                                            ]
                                                        }
                                                        sx={{
                                                            mt: 0.5,
                                                            bgcolor:
                                                                SEGMENT_ACCENTS[
                                                                rec.segment
                                                                ] + "1a",
                                                            color: SEGMENT_ACCENTS[
                                                                rec.segment
                                                            ],
                                                            fontWeight: 600,
                                                        }}
                                                    />
                                                </CollectTableBodyCell>
                                                <CollectTableBodyCell>
                                                    <Typography
                                                        sx={{ fontWeight: 600 }}
                                                    >
                                                        {rec.title}
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            color: "#64748b",
                                                            fontSize: 13,
                                                        }}
                                                    >
                                                        {
                                                            RECOMMENDATION_TYPE_LABELS[
                                                            rec.type
                                                            ]
                                                        }
                                                    </Typography>
                                                </CollectTableBodyCell>
                                                <CollectTableBodyCell>
                                                    <Typography
                                                        sx={{
                                                            color: "#475569",
                                                            fontSize: 13,
                                                            maxWidth: 320,
                                                        }}
                                                    >
                                                        {rec.rationale}
                                                    </Typography>
                                                </CollectTableBodyCell>
                                                <CollectTableBodyCell>
                                                    <Chip
                                                        size="small"
                                                        label={rec.priority}
                                                        sx={{
                                                            textTransform: "capitalize",
                                                            bgcolor:
                                                                (PRIORITY_COLORS[
                                                                    rec.priority
                                                                ] || "#94a3b8") + "1a",
                                                            color:
                                                                PRIORITY_COLORS[
                                                                rec.priority
                                                                ] || "#94a3b8",
                                                            fontWeight: 700,
                                                        }}
                                                    />
                                                </CollectTableBodyCell>
                                                <CollectTableBodyCell align="right">
                                                    <Stack
                                                        direction="row"
                                                        spacing={1}
                                                        justifyContent="flex-end"
                                                    >
                                                        <CollectPrimaryButton
                                                            size="small"
                                                            startIcon={
                                                                <CheckCircleOutline />
                                                            }
                                                            disabled={busy}
                                                            onClick={() =>
                                                                handleApply(rec)
                                                            }
                                                        >
                                                            Apply
                                                        </CollectPrimaryButton>
                                                        <CollectGhostButton
                                                            size="small"
                                                            startIcon={<CloseOutlined />}
                                                            disabled={busy}
                                                            onClick={() => {
                                                                setDismissTarget(rec);
                                                                setDismissReason("");
                                                            }}
                                                        >
                                                            Dismiss
                                                        </CollectGhostButton>
                                                    </Stack>
                                                </CollectTableBodyCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </CollectTableContainer>
                    )}
                </CollectSectionCard>
            </CollectContentArea>

            {/* Dismiss dialog */}
            <Dialog
                open={!!dismissTarget}
                onClose={() => setDismissTarget(null)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Dismiss recommendation</DialogTitle>
                <DialogContent>
                    <Typography sx={{ color: "#64748b", mb: 2 }}>
                        {dismissTarget?.title}
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        minRows={2}
                        label="Reason (optional)"
                        value={dismissReason}
                        onChange={(e) => setDismissReason(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <CollectGhostButton onClick={() => setDismissTarget(null)}>
                        Cancel
                    </CollectGhostButton>
                    <CollectPrimaryButton
                        onClick={handleDismiss}
                        disabled={!!pendingId}
                    >
                        Dismiss
                    </CollectPrimaryButton>
                </DialogActions>
            </Dialog>
        </CollectPageWrapper>
    );
};

export default CollectRecommendations;
